<template>
  <div id="coupon-categories" class="flex flex-col gap-4">
    <form
      class="rule-form grid gap-3 items-end p-3 border rounded-md bg-gray-50"
      @submit.prevent="save"
    >
      <BaseSelectField
        v-model="form.categoryId"
        :options="categoryOptions"
        label="Category"
      />
      <BaseSelectField
        v-model="form.subcategoryId"
        :options="subcategoryOptions"
        :disabled="!form.categoryId || loadingSubcategories"
        label="Subcategory"
      />
      <BaseSelectField
        v-model="form.discountType"
        :options="ruleTypes"
        label="Discount"
      />
      <BaseTextField
        v-model="form.amount"
        type="number"
        step="0.01"
        :label="form.discountType === 'percentage' ? 'Percent (%)' : 'Amount ($)'"
      />
      <div class="flex gap-2 my-1">
        <button
          type="submit"
          class="btn btn-success-button h-10"
          :disabled="!canSave || saving"
        >
          {{ editingId ? "Save" : "Add" }}
        </button>
        <button
          v-if="editingId"
          type="button"
          class="btn btn-dark-button h-10"
          @click="resetForm"
        >
          Cancel
        </button>
      </div>
    </form>

    <div v-if="loading"><BaseSmallSpinner /></div>
    <div
      v-else-if="rules.length === 0"
      class="border border-dashed rounded-md p-6 text-center text-gray-500"
    >
      No categories yet. Pick a category (and optionally a subcategory) to
      discount every product in it.
    </div>
    <table v-else class="w-full text-sm">
      <thead>
        <tr class="text-left text-gray-500 border-b">
          <th class="py-2">Category</th>
          <th class="py-2">Subcategory</th>
          <th class="py-2">Discount</th>
          <th class="py-2"></th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="rule in rules"
          :key="rule.id"
          class="border-b hover:bg-gray-50"
          :class="{ 'bg-yellow-50': rule.id === editingId }"
        >
          <td class="py-2 font-medium">{{ rule.categoryName }}</td>
          <td class="py-2">
            <span v-if="rule.subcategoryId">{{ rule.subCategoryName }}</span>
            <span v-else class="text-gray-500 italic">All subcategories</span>
          </td>
          <td class="py-2">
            <span class="px-2 py-1 rounded bg-green-50 text-green-800">
              {{ ruleText(rule) }}
            </span>
          </td>
          <td class="py-2 text-right whitespace-nowrap">
            <template v-if="confirmDeleteId === rule.id">
              <button class="text-red-600 font-semibold mr-2" @click="remove(rule)">
                Delete
              </button>
              <button class="text-gray-600" @click="confirmDeleteId = null">
                Cancel
              </button>
            </template>
            <template v-else>
              <button class="text-blue-600 mr-3" @click="edit(rule)">Edit</button>
              <button class="text-red-600" @click="confirmDeleteId = rule.id">
                Remove
              </button>
            </template>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
<script>
import {
  getCategories,
  getSubcategories
} from "@/api/store/CategoriesService.js";
import {
  getCouponCategories,
  addCategoryToCoupon,
  updateCategoryFromCoupon,
  deleteCategoryFromCoupon
} from "@/api/store/CouponsService.js";
import { RULE_DISCOUNT_TYPES, ruleDiscountText } from "./couponLabels.js";

const emptyForm = () => ({
  categoryId: "",
  subcategoryId: "",
  discountType: "percentage",
  amount: ""
});

export default {
  name: "CouponCategories",
  props: {
    id: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      rules: [],
      categories: [],
      subcategories: [],
      loading: true,
      loadingSubcategories: false,
      saving: false,
      editingId: null,
      confirmDeleteId: null,
      keepSubcategory: false,
      form: emptyForm()
    };
  },
  computed: {
    ruleTypes() {
      return RULE_DISCOUNT_TYPES;
    },
    categoryOptions() {
      return [{ name: "Select a category", code: "" }].concat(
        this.categories.map(c => ({ name: c.categoryName, code: String(c.id) }))
      );
    },
    subcategoryOptions() {
      return [{ name: "All subcategories", code: "" }].concat(
        this.subcategories.map(s => ({
          name: s.subCategoryName,
          code: String(s.id)
        }))
      );
    },
    canSave() {
      return (
        this.form.categoryId &&
        this.form.discountType &&
        this.form.amount !== "" &&
        Number(this.form.amount) >= 0
      );
    }
  },
  watch: {
    "form.categoryId"(categoryId) {
      // al editar se conserva la subcategoría guardada; al cambiar a mano se limpia
      if (!this.keepSubcategory) {
        this.form.subcategoryId = "";
      }
      this.keepSubcategory = false;
      this.loadSubcategories(categoryId);
    }
  },
  mounted() {
    getCategories().then(res => {
      this.categories = res;
    });
    this.loadRules();
  },
  methods: {
    ruleText(rule) {
      return ruleDiscountText(rule);
    },
    loadRules() {
      this.loading = this.rules.length === 0;
      getCouponCategories(this.id)
        .then(res => {
          this.rules = res;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadSubcategories(categoryId) {
      this.subcategories = [];
      if (!categoryId) return;
      this.loadingSubcategories = true;
      getSubcategories(categoryId)
        .then(res => {
          if (String(categoryId) === String(this.form.categoryId)) {
            this.subcategories = res;
          }
        })
        .finally(() => {
          this.loadingSubcategories = false;
        });
    },
    resetForm() {
      this.editingId = null;
      this.form = emptyForm();
    },
    edit(rule) {
      this.editingId = rule.id;
      this.keepSubcategory =
        String(rule.categoryId) !== String(this.form.categoryId);
      this.form = {
        categoryId: String(rule.categoryId),
        subcategoryId: rule.subcategoryId ? String(rule.subcategoryId) : "",
        discountType: rule.discountType,
        amount: rule.amount
      };
    },
    async save() {
      if (!this.canSave) return;
      const duplicate = this.rules.find(
        rule =>
          rule.id !== this.editingId &&
          String(rule.categoryId) === String(this.form.categoryId) &&
          String(rule.subcategoryId || "") === String(this.form.subcategoryId)
      );
      if (duplicate) {
        this.$toast.error("That category is already in the coupon");
        return;
      }
      this.saving = true;
      const data = {
        categoryId: this.form.categoryId,
        subcategoryId: this.form.subcategoryId || null,
        discountType: this.form.discountType,
        amount: this.form.amount
      };
      try {
        if (this.editingId) {
          await updateCategoryFromCoupon(this.id, this.editingId, data);
          this.$toast.success("Category updated");
        } else {
          await addCategoryToCoupon(this.id, data);
          this.$toast.success("Category added");
        }
        this.resetForm();
        this.loadRules();
        this.$emit("changed");
      } catch (e) {
        this.$toast.error(e.response && e.response.data ? e.response.data : e.message);
      } finally {
        this.saving = false;
      }
    },
    async remove(rule) {
      try {
        await deleteCategoryFromCoupon(this.id, rule.id);
        this.$toast.success("Category removed");
        if (this.editingId === rule.id) this.resetForm();
        this.loadRules();
        this.$emit("changed");
      } catch (e) {
        this.$toast.error("Error removing category");
      } finally {
        this.confirmDeleteId = null;
      }
    }
  }
};
</script>
<style lang="scss" scoped>
.rule-form {
  grid-template-columns: minmax(0, 2fr) minmax(0, 2fr) minmax(0, 1.5fr) minmax(0, 1fr) auto;
  > * {
    min-width: 0;
  }
  ::v-deep select,
  ::v-deep input {
    width: 100%;
    min-width: 0;
  }
}
</style>
